import { clamp, lerpState, smooth, } from "./state";
export function buildCurveProgressSegments(points) {
    if (points.length < 2)
        return [];
    const spans = points.slice(0, -1).map((point, index) => Math.max(points[index + 1].scrollY - point.scrollY, 1));
    const secants = spans.map((span, index) => (points[index + 1].progress - points[index].progress) / span);
    const slopes = points.map((_, index) => {
        if (index === 0)
            return secants[0];
        if (index === points.length - 1)
            return secants[secants.length - 1];
        const before = secants[index - 1];
        const after = secants[index];
        if (before * after <= 0)
            return 0;
        const beforeSpan = spans[index - 1];
        const afterSpan = spans[index];
        const beforeWeight = 2 * afterSpan + beforeSpan;
        const afterWeight = afterSpan + 2 * beforeSpan;
        return (beforeWeight + afterWeight) /
            (beforeWeight / before + afterWeight / after);
    });
    return spans.map((_, index) => ({
        start: points[index].scrollY,
        end: points[index + 1].scrollY,
        from: points[index].progress,
        to: points[index + 1].progress,
        fromSlope: slopes[index],
        toSlope: slopes[index + 1],
    }));
}
export function sampleCurveProgress(segments, scrollY, fallback) {
    if (segments.length === 0 || scrollY < segments[0].start)
        return fallback;
    for (const segment of segments) {
        if (scrollY > segment.end)
            continue;
        const span = segment.end - segment.start;
        const t = clamp((scrollY - segment.start) / span, 0, 1);
        const t2 = t * t;
        const t3 = t2 * t;
        return ((2 * t3 - 3 * t2 + 1) * segment.from +
            (t3 - 2 * t2 + t) * span * segment.fromSlope +
            (-2 * t3 + 3 * t2) * segment.to +
            (t3 - t2) * span * segment.toSlope);
    }
    return fallback;
}
/** Document scroll Y at which `point` on `target` sits at its viewport position. */
export function scrollPosition(target, point, viewportHeight) {
    const targetOffset = point.target === "center" ? target.height / 2 : 0;
    return target.top + targetOffset - viewportHeight * point.viewport;
}
/**
 * Chapter k of N picks (or blends) a state from the authored chapter SEQUENCE:
 * the entry and hold tables interleaved (entry0, hold0, entry1, hold1, …). The
 * prototype authored exactly three chapters, so the sequence has six states;
 * N=3 lands on them exactly, and other counts walk the same sequence evenly so
 * curve progress never runs backwards between one chapter's hold and the
 * next chapter's entry.
 */
export function chapterTablePosition(phase, k, n, sequenceLength) {
    const last = Math.max(sequenceLength - 1, 0);
    const slots = Math.max(2 * n - 1, 1);
    const slot = clamp(2 * k + (phase === "hold" ? 1 : 0), 0, slots);
    const u = (slot / slots) * last;
    const index = Math.min(Math.floor(u), last);
    const toIndex = Math.min(Math.ceil(u), last);
    const frac = u - index;
    if (toIndex === index || frac <= 1e-9)
        return { index };
    return { index, blend: { toIndex, t: frac } };
}
/** The authored chapter sequence: entry and hold tables interleaved. */
export function chapterSequence(tables) {
    const length = Math.max(tables.entry.length, tables.hold.length);
    const sequence = [];
    for (let i = 0; i < length; i += 1) {
        if (tables.entry[i])
            sequence.push(tables.entry[i]);
        if (tables.hold[i])
            sequence.push(tables.hold[i]);
    }
    return sequence;
}
function anchorOf(point) {
    return point.anchor ?? "self";
}
/**
 * Instantiate the preset's step templates against the measured waypoints, in
 * waypoint (document) order. Chapters are numbered across every chapter target.
 */
export function expandWaypointSteps(preset, targets) {
    const chapterCount = targets.filter((target) => target.role === "chapter").length;
    let chapterIndex = 0;
    const steps = [];
    for (const target of targets) {
        if (!preset.roles.includes(target.role))
            continue;
        const templates = preset.steps.filter((step) => step.role === target.role);
        const anchors = {
            self: target.self,
            "previous-block": target.previousBlock,
        };
        const isChapter = target.role === "chapter";
        const k = chapterIndex;
        const isLastChapter = isChapter && k === chapterCount - 1;
        for (const template of templates) {
            const resolved = resolveTemplateState(preset, template, k, chapterCount);
            if (!resolved)
                continue;
            const end = isLastChapter && template.finalEnd ? template.finalEnd : template.end;
            steps.push({
                role: target.role,
                blockId: target.blockId,
                stateId: resolved.stateId,
                blend: resolved.blend,
                start: template.start,
                end,
                shapeWindow: template.shapeWindow,
                release: template.release,
                releaseTop: template.releaseTop,
                timedRevealDurationMs: template.timedRevealDurationMs,
                timedRevealReverseDurationMs: template.timedRevealReverseDurationMs,
                timedRevealReverseStart: template.timedRevealReverseStart,
                timedRevealReverseScrollBound: template.timedRevealReverseScrollBound,
                startTarget: anchors[anchorOf(template.start)],
                endTarget: anchors[anchorOf(end)],
                targets: anchors,
            });
        }
        if (isChapter)
            chapterIndex += 1;
    }
    return steps;
}
function resolveTemplateState(preset, template, k, n) {
    if (template.stateId !== undefined)
        return { stateId: template.stateId };
    if (!preset.chapterTables)
        return null;
    const sequence = chapterSequence(preset.chapterTables);
    if (sequence.length === 0)
        return null;
    const position = chapterTablePosition(template.chapterPhase, k, n, sequence.length);
    return {
        stateId: sequence[position.index],
        blend: position.blend
            ? { toStateId: sequence[position.blend.toIndex], t: position.blend.t }
            : undefined,
    };
}
/**
 * Turn resolved steps into scroll ranges. Clamping mirrors the prototype
 * exactly: a step never starts before the previous one ends, always spans at
 * least 1px, and a releasing step finishes no later than its release point.
 */
export function measureTimeline(steps, resolvedStates, viewportHeight, initialState) {
    let releaseY = Infinity;
    let releaseTopY = Infinity;
    let timedReveal = null;
    let previousEnd = -Infinity;
    const measured = steps.flatMap((step) => {
        const from = resolvedStates.get(step.stateId);
        if (!from)
            return [];
        let state = from;
        if (step.blend) {
            const to = resolvedStates.get(step.blend.toStateId);
            if (!to)
                return [];
            state = lerpState(from, to, step.blend.t);
        }
        const targetForPoint = (point) => step.targets[anchorOf(point)];
        // Viewport-relative trigger formulas can overlap at tall aspect ratios.
        // Keep the authored gaps, but never let a later transition begin before
        // the previous one has finished: entering a step partway through would
        // otherwise produce a visible one-frame jump at the chapter boundary.
        const start = Math.max(previousEnd, scrollPosition(step.startTarget, step.start, viewportHeight));
        const requestedRelease = step.release
            ? scrollPosition(targetForPoint(step.release), step.release, viewportHeight)
            : null;
        const requestedReleaseTop = step.releaseTop
            ? scrollPosition(targetForPoint(step.releaseTop), step.releaseTop, viewportHeight)
            : requestedRelease;
        const requestedEnd = scrollPosition(step.endTarget, step.end, viewportHeight);
        // A releasing step must finish before its canvas unpins. On tall
        // viewports the authored end point can otherwise fall below the release
        // point, leaving the final horizontal state physically unreachable.
        const end = Math.max(start + 1, requestedRelease === null
            ? requestedEnd
            : Math.min(requestedEnd, requestedRelease));
        previousEnd = end;
        if (requestedRelease !== null) {
            releaseY = Math.max(requestedRelease, end);
            releaseTopY = requestedReleaseTop ?? releaseY;
        }
        if (step.timedRevealDurationMs !== undefined) {
            timedReveal = {
                startY: start,
                reverseStartY: step.timedRevealReverseStart
                    ? Math.max(start, scrollPosition(targetForPoint(step.timedRevealReverseStart), step.timedRevealReverseStart, viewportHeight))
                    : start,
                durationMs: step.timedRevealDurationMs,
                reverseDurationMs: step.timedRevealReverseDurationMs ?? step.timedRevealDurationMs,
                reverseScrollBound: step.timedRevealReverseScrollBound ?? false,
                state,
            };
        }
        return [{
                state,
                stateId: step.stateId,
                blockId: step.blockId,
                role: step.role,
                start,
                end,
                shapeWindow: step.shapeWindow,
            }];
    });
    const firstCurveStep = measured.findIndex((step) => step.state.curveStrength > 0);
    const curveProgressSegments = firstCurveStep < 0
        ? []
        : buildCurveProgressSegments([
            {
                scrollY: measured[firstCurveStep].start,
                progress: initialState?.curveProgress ?? 0,
            },
            ...measured.slice(firstCurveStep).map((step) => ({
                scrollY: step.end,
                progress: step.state.curveProgress,
            })),
        ]);
    return { measured, releaseY, releaseTopY, timedReveal, curveProgressSegments };
}
/** The visual state at a scroll offset, or null without an initial state. */
export function stateAtScroll(measured, initialState, segments, scrollY) {
    let state = initialState;
    if (!state)
        return null;
    for (const step of measured) {
        if (scrollY <= step.start) {
            return {
                ...state,
                curveProgress: sampleCurveProgress(segments, scrollY, state.curveProgress),
            };
        }
        if (scrollY < step.end) {
            const progress = clamp((scrollY - step.start) / (step.end - step.start), 0, 1);
            const [shapeStart, shapeEnd] = step.shapeWindow ?? [0, 1];
            const shapeProgress = clamp((progress - shapeStart) / Math.max(shapeEnd - shapeStart, 0.001), 0, 1);
            const interpolated = lerpState(state, step.state, smooth(progress), smooth(shapeProgress));
            return {
                ...interpolated,
                curveProgress: sampleCurveProgress(segments, scrollY, interpolated.curveProgress),
            };
        }
        state = step.state;
    }
    return {
        ...state,
        curveProgress: sampleCurveProgress(segments, scrollY, state.curveProgress),
    };
}
/**
 * Index of the step whose range contains `scrollY`, else the last step already
 * passed; -1 while still ahead of the first step.
 */
export function activeStepIndex(measured, scrollY) {
    let index = -1;
    for (let i = 0; i < measured.length; i += 1) {
        if (scrollY > measured[i].start)
            index = i;
        else
            break;
    }
    return index;
}
//# sourceMappingURL=timeline.js.map